import { useEffect, useRef } from "react";
import { toast } from "sonner";

type BIPEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

const DISMISS_KEY = "pwa-install-dismissed-until";
const INTERVAL_MS = 5 * 60 * 1000; // every 5 minutes

export const InstallPwaPrompt = () => {
  const deferredRef = useRef<BIPEvent | null>(null);
  const installedRef = useRef(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      // @ts-ignore iOS
      window.navigator.standalone === true;
    if (isStandalone) return;

    const onBIP = (e: Event) => {
      e.preventDefault();
      deferredRef.current = e as BIPEvent;
    };
    const onInstalled = () => {
      installedRef.current = true;
      deferredRef.current = null;
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);

    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

    const show = () => {
      if (installedRef.current) return;
      const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (Date.now() < until) return;

      toast("Download the app now", {
        duration: 10000,
        action: {
          label: "Install",
          onClick: async () => {
            if (deferredRef.current) {
              await deferredRef.current.prompt();
              await deferredRef.current.userChoice;
              deferredRef.current = null;
            } else if (isIOS) {
              toast("On iPhone: tap Share → Add to Home Screen", { duration: 8000 });
            } else {
              toast("Use your browser menu → Install app", { duration: 8000 });
            }
          },
        },
        onDismiss: () => {
          localStorage.setItem(DISMISS_KEY, String(Date.now() + 60 * 60 * 1000));
        },
      });
    };

    const first = setTimeout(show, 20000);
    const interval = setInterval(show, INTERVAL_MS);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
      clearTimeout(first);
      clearInterval(interval);
    };
  }, []);

  return null;
};
